import React, { useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Card,
  Space,
  Pagination,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  usePositionTasks,
  usePositionTaskMutations,
  useCreateTemplateTask,
} from "../hooks/useTasks";
import type { Task } from "../types/task";
import { getTagsColumn } from "./table/columns/index";
import { TaskModal } from "./modals/TaskModal";
import { TemplateModal } from "./modals/TemplateModal";
import { useAuth } from "../../utils/Auth";
const useTranslation = () => {
  return { t: (text: any) => text };
};
const PAGE_SIZE = 10;
interface PositionTasksProps {
  positionId: number;
}

const PositionTasks: React.FC<PositionTasksProps> = ({ positionId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [name, setName] = useState("");

  const [taskForm] = Form.useForm<any>();

  // Queries
  const { data: tasksData, isLoading } = usePositionTasks(
    positionId,
    currentPage,
  );
  const canEdit = user?.is_superuser || false;

  const createTemplateTask = useCreateTemplateTask({
    options: {
      onSuccess: (data: any) => {
        taskForm.setFieldsValue({
          template_task_id: data.id,
        });
      },
    },
  });

  // Mutations
  const { remove } = usePositionTaskMutations(positionId);

  // Handle modal open for create/edit
  const handleOpenModal = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      taskForm.setFieldsValue({
        template_task_id: task.template_task.id,
        priority: task.priority,
      });
    } else {
      setEditingTask(null);
      taskForm.resetFields();
    }
    setIsModalVisible(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditingTask(null);
    taskForm.resetFields();
  };

  // Handle delete confirmation
  const handleDelete = (task: Task) => {
    Modal.confirm({
      title: t("Tem certeza que deseja deletar essa terefa?"),
      content: t("Essa ação não pode ser desfeita!"),
      icon: <ExclamationCircleOutlined />,
      okText: t("Delete"),
      okType: "danger",
      cancelText: t("Cancel"),
      onOk: async () => {
        await remove.mutateAsync(task.id);
      },
    });
  };

  // Table columns
  const columns = [
    {
      title: t("Tarefa"),
      dataIndex: ["template_task", "name"],
      key: "name",
      width: "30%",
    },
    getTagsColumn({ t }),
    ...(canEdit
      ? [
          {
            title: t("Ações"),
            key: "actions",
            width: "15%",
            render: (_: any, record: Task) => {
              return (
                <Space size="middle">
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => handleOpenModal(record)}
                    size="small"
                  />
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    onClick={() => handleDelete(record)}
                    size="small"
                  />
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <TaskModal
        form={taskForm}
        positionId={positionId}
        isModalVisible={isModalVisible}
        editingTask={editingTask}
        setIsTemplateModalVisible={(search?: string) => {
          if (search) setName(search);
          setIsTemplateModalVisible(true);
        }}
        handleCloseModal={handleCloseModal}
      />
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div />
            {canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenModal()}
              >
                {t("Adicionar")}
              </Button>
            )}
          </div>

          <Table
            columns={columns}
            dataSource={tasksData?.results || []}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            locale={{
              emptyText: (
                <Empty description={t("Sem tarefas para esse cargo")} />
              ),
            }}
          />

          {tasksData && (tasksData.count || 0) > PAGE_SIZE && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Pagination
                current={currentPage}
                total={tasksData.count}
                pageSize={PAGE_SIZE}
                onChange={setCurrentPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </Space>
      </Card>
      <TemplateModal
        name={name}
        isTemplateModalVisible={isTemplateModalVisible}
        setIsTemplateModalVisible={setIsTemplateModalVisible}
        createTemplateTask={createTemplateTask}
        parentForm={taskForm}
      />
    </>
  );
};
export { PositionTasks };
